import React from "react";
import { motion } from "framer-motion";
import { Target, MessageCircle, Sparkles, TrendingUp, BarChart3 } from "lucide-react";

/**
 * AI Team Hero — circular constellation of AI agents around the brand mark.
 *
 * All agent stats are sourced from the dashboard summary via the
 * `agentStats` prop. When a value is missing or zero the card shows an
 * "Idle" badge so the UI never lies about activity.
 */
const AGENT_DEFS = [
  { key: "lead",      label: "Lead Hunter AI",       detail: "Capturing new leads",      icon: Target,        color: "from-violet-500 to-indigo-500", pos: "top-2 left-4" },
  { key: "upsell",    label: "Upsell AI",            detail: "Surfacing upsell offers",  icon: TrendingUp,    color: "from-pink-500 to-rose-500",      pos: "top-2 right-4" },
  { key: "followup",  label: "Follow-Up AI",         detail: "Nurturing warm leads",     icon: MessageCircle, color: "from-emerald-500 to-teal-500",   pos: "bottom-28 left-0" },
  { key: "analytics", label: "Analytics AI",         detail: "Optimizing conversion",    icon: BarChart3,     color: "from-sky-500 to-blue-500",       pos: "bottom-28 right-0" },
  { key: "recovery",  label: "Revenue Recovery AI",  detail: "Recovering lost revenue",  icon: Sparkles,      color: "from-orange-500 to-amber-500",   pos: "bottom-2 left-1/2 -translate-x-1/2" },
];

export default function AiTeamHero({ logoUrl, stats, agentStats = {} }) {
  const fmt = (v, suffix = "") => {
    if (v == null || v === 0 || v === "0") return "Idle";
    return suffix ? `${v}${suffix}` : v;
  };
  const agentStatFor = (key) => {
    switch (key) {
      case "lead":      return fmt(agentStats.leads_today, " leads today");
      case "upsell":    return fmt(agentStats.upsell_opportunities, " opps");
      case "followup":  return fmt(agentStats.active_chats, " active chats");
      case "analytics": return agentStats.ai_accuracy != null ? `${Math.round(agentStats.ai_accuracy)}% accuracy` : "Idle";
      case "recovery":  return agentStats.revenue_recovered ? `$${Math.round(agentStats.revenue_recovered).toLocaleString()} recovered` : "Idle";
      default:          return "Idle";
    }
  };

  return (
    <div className="relative rounded-3xl border border-violet-500/15 bg-gradient-to-br from-violet-50 via-white to-pink-50 p-6 lg:p-8 overflow-hidden" data-testid="ai-team-hero">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-30" style={{
          background: "radial-gradient(60% 50% at 50% 50%, rgba(139,92,246,0.25), transparent 70%)",
        }} />
      </div>

      <div className="relative flex items-center justify-between mb-4">
        <div className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur border border-violet-200 px-3 py-1 text-xs text-violet-700 font-medium">
          <Sparkles className="h-3.5 w-3.5" /> AI TEAM AT WORK
        </div>
        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 font-medium"><span className="pulse-dot" /> Live</span>
      </div>

      <div className="relative h-[440px]">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.7 }}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        >
          <div className="relative">
            <div className="absolute inset-0 rounded-full blur-2xl ashra-gradient-bg opacity-40 scale-150" />
            <div className="relative h-36 w-36 rounded-full bg-white shadow-2xl shadow-violet-500/25 ring-4 ring-white grid place-items-center overflow-hidden">
              <img src={logoUrl} alt="Ashra AI" className="h-full w-full object-contain p-2" />
            </div>
            <motion.div
              className="absolute -inset-8 rounded-full border-2 border-violet-300/40"
              animate={{ rotate: 360 }}
              transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
              style={{ borderTopColor: "rgba(236,72,153,0.6)", borderRightColor: "rgba(139,92,246,0.6)" }}
            />
          </div>
        </motion.div>

        {AGENT_DEFS.map((a, i) => (
          <motion.div
            key={a.key}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 * i + 0.2, duration: 0.5 }}
            className={`absolute ${a.pos} w-52`}
          >
            <div className="rounded-2xl bg-white/95 backdrop-blur border border-black/5 shadow-lg shadow-violet-500/5 p-3">
              <div className="flex items-center gap-2">
                <div className={`h-9 w-9 rounded-xl bg-gradient-to-br ${a.color} grid place-items-center text-white shadow-inner`}>
                  <a.icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-bold text-zinc-900 leading-tight truncate">{a.label}</p>
                  <p className="text-[11px] text-zinc-500 truncate">{a.detail}</p>
                </div>
              </div>
              <div className="mt-2 text-[11px] text-violet-600 font-semibold">{agentStatFor(a.key)}</div>
            </div>
          </motion.div>
        ))}
      </div>

      {stats && (
        <div className="relative mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
          {stats.map((s) => (
            <div key={s.label} className="rounded-xl bg-white/80 backdrop-blur border border-black/5 p-3">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">{s.label}</p>
              <p className="font-display text-xl font-extrabold text-zinc-900">{s.value}</p>
              {s.delta && <p className="text-[10px] text-emerald-600 font-medium">{s.delta}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
